import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Seg } from '@/components/ui/seg';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAddDrop } from '@/hooks/useAddDrop';
import { useWaiverClaims } from '@/hooks/useWaiverClaims';
import { useLeague } from '@/contexts/LeagueContext';
import { useProbablePitchers } from '@/hooks/useProbablePitchers';
import AddDropDialog, { type WaiverMode } from '@/components/mlb-beta/AddDropDialog';
import PlayerCompareDialog from '@/components/mlb-beta/PlayerCompareDialog';
import ProbablePitcherBadge from '@/components/mlb-beta/ProbablePitcherBadge';
import { Download, UserPlus, UserMinus, Lock, ArrowLeftRight, GitCompareArrows } from 'lucide-react';
import { cn } from '@/lib/utils';
import MLBPlayerAvatar, { positionColors } from '@/components/mlb-beta/MLBPlayerAvatar';
import { formatPositionDisplay } from '@/lib/positionEligibility';
import StatsColumns, { type StatsRow } from '@/components/mlb-beta/StatsColumns';
import {
  type SeasonStats,
  BATTER_COLUMNS, PITCHER_COLUMNS, PITCHER_POSITIONS,
} from '@/components/mlb-beta/statsTableDefs';
import { useScoringRules } from '@/contexts/ScoringRulesContext';
import { useTimeFilteredRankings } from '@/hooks/useTimeFilteredRankings';
import { type PointsTimeFilter, TIME_FILTER_OPTIONS } from '@/lib/timeFilter';
import { MLB_TEAM_ID_TO_ABBR } from '@/data/mlbTeams';

interface PlayerRow {
  id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: string[] | null;
  mlb_team_abbreviation: string | null;
  mlb_team_name: string | null;
  mlb_id: number | null;
  status: string;
  headshot_url: string | null;
  adp: number;
  stats: SeasonStats | null;
}

const POSITION_GROUPS = [
  { value: 'All', label: 'All' },
  { value: 'Hitters', label: 'Hitters' },
  { value: 'Pitchers', label: 'Pitchers' },
  { value: 'ProbablePitchers', label: 'Probable Pitchers' },
  { value: 'TwoStart', label: '2-Start SP' },
  { value: 'C', label: 'C' },
  { value: '1B', label: '1B' },
  { value: '2B', label: '2B' },
  { value: '3B', label: '3B' },
  { value: 'SS', label: 'SS' },
  { value: 'OF', label: 'OF' },
  { value: 'DH', label: 'DH' },
  { value: 'SP', label: 'SP' },
  { value: 'RP', label: 'RP' },
];

const HITTER_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'OF', 'DH'];

const FILTER_STORAGE_KEY = 'mlb-players-filters';
// Bump this when filter shape or defaults change to clear stale sessions
const FILTER_VERSION = 6;

const saveFilters = (filters: Record<string, any>) => {
  try { sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({ ...filters, _v: FILTER_VERSION })); } catch {}
};

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const MLBBetaPlayers = ({ onNavigateToTrades, onNavigateToPlayer }: { onNavigateToTrades?: () => void; onNavigateToPlayer?: (playerId: string) => void }) => {
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const { toast } = useToast();
  const stored = useMemo(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY) || '{}');
      // Discard stale sessions from older app versions
      if (raw._v !== FILTER_VERSION) return {};
      return raw;
    } catch { return {}; }
  }, []);
  const [search, setSearch] = useState(stored.search || '');
  const [posFilter, setPosFilter] = useState(stored.posFilter || 'All');
  const [ownershipFilter, setOwnershipFilter] = useState<'all' | 'available' | 'taken'>(() => {
    // Collapsed 4-state filter (MLB-064) back to 3-state per Adam's mobile UX feedback —
    // "On my team" / "On other teams" rarely needed, both fold into "Taken". Migrate
    // any persisted 'mine' / 'others' to 'taken' transparently.
    const v = stored.ownershipFilter;
    if (v === 'mine' || v === 'others') return 'taken';
    if (v === 'all' || v === 'available' || v === 'taken') return v;
    return 'available';
  });
  const [playerType, setPlayerType] = useState<'all' | 'batters' | 'pitchers'>(stored.playerType || 'all');
  const [statView, setStatView] = useState<'season' | 'pergame'>(stored.statView || 'season');
  const [sortKey, setSortKey] = useState<string | null>(stored.sortKey ?? 'SPTS');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(stored.sortDir || 'desc');
  const [pointsTimeFilter, setPointsTimeFilter] = useState<PointsTimeFilter>(stored.pointsTimeFilter || 'season');

  useEffect(() => {
    saveFilters({ search, posFilter, ownershipFilter, playerType, statView, sortKey, sortDir, pointsTimeFilter });
  }, [search, posFilter, ownershipFilter, playerType, statView, sortKey, sortDir, pointsTimeFilter]);

  // Add/drop state
  const addDrop = useAddDrop();
  const { activeLeague } = useLeague();
  const { scoringRules } = useScoringRules();
  const { rankings, loading: rankingsLoading } = useTimeFilteredRankings(scoringRules, pointsTimeFilter, 2026);
  const waiverClaims = useWaiverClaims();
  const waiverMode = (activeLeague?.waiver_type as WaiverMode) || 'fcfs';
  const [addDropOpen, setAddDropOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  // Compare mode gates checkbox-column visibility. Default off so the column
  // doesn't show orange L-bracket frames on every row for users who never use
  // Compare. Click the Compare button to toggle on; click Cancel to exit.
  const [compareMode, setCompareMode] = useState(false);
  // MLB-068 — multi-select state for the Compare workflow. Compare CTA is selection-gated (≥2 required); first 2 selected become the dialog's initial A/B.
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<string>>(new Set());
  const togglePlayerSelection = (id: string) => {
    setSelectedPlayerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const [addTarget, setAddTarget] = useState<{ id: string; full_name: string; position_abbreviation: string; position_eligibility?: string[] | null; mlb_team_abbreviation: string | null; headshot_url: string | null; player_status?: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<typeof addDrop.roster[number] | null>(null);

  const { byPlayerId } = useProbablePitchers();

  // Game info per team abbreviation for today's matchup display
  type GameInfo = { opponent: string; isHome: boolean; status: string };
  const [gameInfoMap, setGameInfoMap] = useState<Map<string, GameInfo>>(new Map());
  const [probablePitcherMlbIds, setProbablePitcherMlbIds] = useState<Set<number>>(new Set());
  const [twoStartPitcherIds, setTwoStartPitcherIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchGames = async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data: games } = await supabase
        .from('game_schedule')
        .select('home_team_id, away_team_id, status, home_probable_pitcher_mlb_id, away_probable_pitcher_mlb_id')
        .eq('game_date', today);
      if (!games || games.length === 0) { setGameInfoMap(new Map()); setProbablePitcherMlbIds(new Set()); return; }
      const info = new Map<string, GameInfo>();
      const ppIds = new Set<number>();
      for (const g of games) {
        const homeAbbr = MLB_TEAM_ID_TO_ABBR[g.home_team_id];
        const awayAbbr = MLB_TEAM_ID_TO_ABBR[g.away_team_id];
        const status = g.status || 'scheduled';
        if (homeAbbr) info.set(homeAbbr, { opponent: awayAbbr || '??', isHome: true, status });
        if (awayAbbr) info.set(awayAbbr, { opponent: homeAbbr || '??', isHome: false, status });
        if (g.home_probable_pitcher_mlb_id) ppIds.add(g.home_probable_pitcher_mlb_id);
        if (g.away_probable_pitcher_mlb_id) ppIds.add(g.away_probable_pitcher_mlb_id);
      }
      setGameInfoMap(info);
      setProbablePitcherMlbIds(ppIds);
    };
    fetchGames();
  }, []);

  // Compute 2-start pitchers from game_schedule for the NEXT full Mon–Sun week
  useEffect(() => {
    const fetchTwoStarts = async () => {
      const now = new Date();
      const day = now.getDay(); // 0=Sun … 6=Sat
      // On Monday use this week; on Tue-Sun use next Monday
      const daysUntilMon = day <= 1 ? 1 - day : 8 - day;
      const monday = new Date(now);
      monday.setDate(now.getDate() + daysUntilMon);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const monStr = monday.toISOString().slice(0, 10);
      const sunStr = sunday.toISOString().slice(0, 10);

      const { data: weekGames } = await supabase
        .from('game_schedule')
        .select('home_probable_pitcher_mlb_id, away_probable_pitcher_mlb_id')
        .gte('game_date', monStr)
        .lte('game_date', sunStr);

      if (!weekGames || weekGames.length === 0) { setTwoStartPitcherIds(new Set()); return; }

      // Count starts per mlb_id
      const startCounts = new Map<number, number>();
      for (const g of weekGames) {
        if (g.home_probable_pitcher_mlb_id) startCounts.set(g.home_probable_pitcher_mlb_id, (startCounts.get(g.home_probable_pitcher_mlb_id) || 0) + 1);
        if (g.away_probable_pitcher_mlb_id) startCounts.set(g.away_probable_pitcher_mlb_id, (startCounts.get(g.away_probable_pitcher_mlb_id) || 0) + 1);
      }
      const twoStartMlbIds = new Set<number>();
      for (const [mlbId, count] of startCounts) {
        if (count >= 2) twoStartMlbIds.add(mlbId);
      }
      // Map mlb_ids back to player UUIDs using the players list
      const ids = new Set<string>();
      for (const p of players) {
        if (p.mlb_id && twoStartMlbIds.has(p.mlb_id)) ids.add(p.id);
      }
      setTwoStartPitcherIds(ids);
    };
    if (players.length > 0) fetchTwoStarts();
  }, [players]);

  useEffect(() => {
    let cancelled = false;
    const STATS_COLS = [
      'player_id', 'season',
      'games_played', 'at_bats', 'hits', 'runs_scored', 'doubles', 'triples',
      'home_runs', 'rbi', 'walks_drawn', 'strikeouts', 'stolen_bases', 'caught_stealing',
      'gidp', 'pitcher_wins', 'pitcher_losses', 'pitcher_strikeouts', 'saves',
      'innings_pitched', 'earned_runs', 'walks_allowed', 'home_runs_allowed', 'hits_allowed',
      'is_pitcher', 'games_started_pitching', 'blown_saves', 'wild_pitches', 'hit_batsmen',
      'hit_by_pitch', 'balks', 'passed_balls', 'errors', 'outfield_assists', 'shutouts',
      'complete_games', 'holds', 'left_on_base', 'sac_flies',
    ].join(',');
    const STATS_SELECT = `player_season_stats!left(${STATS_COLS})`;

    const fetchPlayers = async () => {
      setError(null);
      setLoading(true);
      try {
        let allData: any[] = [];

        // Probable-pitcher bypass: a pitcher MLB has named as today's probable starter is
        // findable/addable even if players.status hasn't caught up. MLB posts the probable start
        // BEFORE it posts the 40-man recall, so a just-called-up starter reads a status outside
        // the allowlist (e.g. "Reassigned to Minors") for a window that spans his start — which
        // otherwise hides a player we KNOW is pitching today. game_schedule already carries the
        // probable-pitcher mlb_ids, so OR them into the allowlist. Purely additive: everything
        // the status allowlist showed still shows.
        // ±1-day window on game_date: a night game's MLB game_date is the venue-local date, which
        // can trail the client's UTC date by a day (a 6:45pm PT start is dated the 27th but is the
        // 28th in UTC), so matching UTC-today alone drops the starter after 00:00 UTC — right in
        // the evening lineup-setting window before first pitch. A ±1-day window keeps today's
        // announced starters findable across that boundary; adjacent-day starters are all real
        // addable pitchers, so it is safe to be inclusive here.
        const dayOffset = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
        const { data: bypassGames } = await supabase
          .from('game_schedule')
          .select('home_probable_pitcher_mlb_id, away_probable_pitcher_mlb_id')
          .gte('game_date', dayOffset(-1))
          .lte('game_date', dayOffset(1));
        const probableStarterMlbIds = new Set<number>();
        for (const g of bypassGames || []) {
          if (g.home_probable_pitcher_mlb_id) probableStarterMlbIds.add(g.home_probable_pitcher_mlb_id);
          if (g.away_probable_pitcher_mlb_id) probableStarterMlbIds.add(g.away_probable_pitcher_mlb_id);
        }
        const STATUS_ALLOWLIST = ['Active', 'Injured 10-Day', 'Injured 15-Day', 'Injured 60-Day', 'Injured 7-Day', 'Injured - Full Season', 'Day-To-Day'];
        // PostgREST or(): allowlist values are quoted (spaces/hyphens); mlb_ids are integers.
        const statusInList = STATUS_ALLOWLIST.map((s) => `"${s}"`).join(',');
        const orFilter = probableStarterMlbIds.size > 0
          ? `status.in.(${statusInList}),mlb_id.in.(${[...probableStarterMlbIds].join(',')})`
          : null;

        let from = 0;
        const pageSize = 1000;
        while (true) {
          let query = supabase
            .from('players')
            .select(`
              id, full_name, position_abbreviation, position_eligibility,
              mlb_team_abbreviation, mlb_team_name, mlb_id, status, headshot_url,
              adp,
              ${STATS_SELECT}
            `)
            .eq('sport', 'mlb');
          query = orFilter ? query.or(orFilter) : query.in('status', STATUS_ALLOWLIST);
          const { data: batch, error: batchError } = await query
            .eq('player_season_stats.season', 2026)
            .order('adp', { ascending: true })
            .range(from, from + pageSize - 1);
          if (batchError) throw batchError;
          allData = allData.concat(batch || []);
          if (!batch || batch.length < pageSize) break;
          from += pageSize;
        }

        if (cancelled) return;

        const mapped: PlayerRow[] = allData.map((p: any) => {
          const statsArr = p.player_season_stats;
          const stats: SeasonStats | null = Array.isArray(statsArr) && statsArr.length > 0
            ? statsArr[0]
            : statsArr && !Array.isArray(statsArr) ? statsArr : null;
          return { ...p, player_season_stats: undefined, stats };
        });

        setPlayers(mapped);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('[MLBBetaPlayers] fetch failed:', err);
        Sentry.captureException(err, { tags: { component: 'MLBBetaPlayers', op: 'fetchPlayers' } });
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    };
    fetchPlayers();
    return () => { cancelled = true; };
  }, [loadAttempt]);

  // 10-second "still loading" toast so users know the app is alive on slow connections
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      toast({
        title: 'Still loading…',
        description: 'Fetching players over a slow connection. Hang tight.',
      });
    }, 10000);
    return () => clearTimeout(t);
  }, [loading, loadAttempt, toast]);

  const matchesPos = (pos: string, filter: string, playerId?: string, eligibility?: string[] | Record<string, number> | null, mlbId?: number | null) => {
    if (filter === 'All') return true;
    if (filter === 'TwoStart') return playerId ? twoStartPitcherIds.has(playerId) : false;
    if (filter === 'ProbablePitchers') return mlbId ? probablePitcherMlbIds.has(mlbId) : false;
    const eligArr = Array.isArray(eligibility) ? eligibility : eligibility ? Object.keys(eligibility) : [];
    const allPos = new Set([pos, ...eligArr]);
    if (filter === 'Hitters') return [...allPos].some(p => HITTER_POSITIONS.includes(p) || p === 'TWP');
    if (filter === 'Pitchers') return [...allPos].some(p => PITCHER_POSITIONS.includes(p));
    if (filter === 'OF') return [...allPos].some(p => ['LF', 'CF', 'RF', 'OF'].includes(p));
    return allPos.has(filter);
  };

  // Determine if showing pitchers based on filter or toggle
  const isPitcherView = playerType === 'pitchers' ||
    (['SP', 'RP', 'ProbablePitchers'].includes(posFilter) && playerType !== 'batters' && playerType !== 'all');
  // SAB-281 — reverted MLB-065's column hiding on the All tab. The empty
  // columns array left StatsColumns' middle flex-1 div with no headers/cells
  // but full available width, producing a huge gap between OWNER and SAB PTS
  // for every fresh-session user (All is the default tab). MLB-065 is still
  // open for proper re-implementation — likely changing the default tab to
  // Batters per the original ticket suggestion.
  const columns = playerType === 'all'
    ? BATTER_COLUMNS
    : (isPitcherView ? PITCHER_COLUMNS : BATTER_COLUMNS);

  const filtered = useMemo(() => {
    let list = players;
    // Apply position filter
    list = list.filter(p => matchesPos(p.position_abbreviation, posFilter, p.id, p.position_eligibility, p.mlb_id));
    // Apply batter/pitcher toggle
    if (playerType === 'batters') {
      list = list.filter(p => !p.stats?.is_pitcher || HITTER_POSITIONS.includes(p.position_abbreviation));
    } else if (playerType === 'pitchers') {
      list = list.filter(p => p.stats?.is_pitcher || PITCHER_POSITIONS.includes(p.position_abbreviation));
    }
    if (search) {
      const q = normalizeSearchText(search);
      list = list.filter(p =>
        normalizeSearchText(p.full_name).includes(q) ||
        normalizeSearchText(p.mlb_team_name || '').includes(q) ||
        normalizeSearchText(p.mlb_team_abbreviation || '').includes(q)
      );
    }
    if (ownershipFilter === 'available') {
      list = list.filter(p => addDrop.isFreeAgent(p.id));
    } else if (ownershipFilter === 'taken') {
      list = list.filter(p => !addDrop.isFreeAgent(p.id));
    }
    return list;
  }, [players, search, posFilter, ownershipFilter, playerType, addDrop, twoStartPitcherIds, probablePitcherMlbIds]);

  // MLB-062 — when a search is active inside a restricted Batters/Pitchers tab,
  // count the matches in the OTHER tab so we can prompt "X more in [other tab]".
  // Re-runs the search pipeline ignoring the playerType filter, then subtracts.
  const crossTabMatch = useMemo(() => {
    if (!search || playerType === 'all') return null;
    const q = normalizeSearchText(search);
    const matchesSearch = (p: PlayerRow) =>
      normalizeSearchText(p.full_name).includes(q) ||
      normalizeSearchText(p.mlb_team_name || '').includes(q) ||
      normalizeSearchText(p.mlb_team_abbreviation || '').includes(q);
    let other = 0;
    for (const p of players) {
      if (!matchesPos(p.position_abbreviation, posFilter, p.id, p.position_eligibility, p.mlb_id)) continue;
      if (!matchesSearch(p)) continue;
      const isPitcher = p.stats?.is_pitcher || PITCHER_POSITIONS.includes(p.position_abbreviation);
      if (playerType === 'batters' && isPitcher) other++;
      else if (playerType === 'pitchers' && !isPitcher) other++;
    }
    return other > 0 ? { count: other, other: playerType === 'batters' ? 'pitchers' : 'batters' as const } : null;
  }, [search, playerType, posFilter, players, twoStartPitcherIds, probablePitcherMlbIds]);

  // Read pre-computed TP and games played from rankings (season or time-filtered)
  const { tpMap, gpMap } = useMemo(() => {
    const tp = new Map<string, number>();
    const gp = new Map<string, number>();
    for (const r of rankings) {
      tp.set(r.player_id, r.net_points);
      gp.set(r.player_id, r.games_played);
    }
    return { tpMap: tp, gpMap: gp };
  }, [rankings]);

  const getTP = (p: PlayerRow) => tpMap.get(p.id) || 0;

  // SAB-364: in 7d/14d/30d views, GP must come from the windowed gpMap, not
  // player_season_stats.games_played (season total). Substitute games_played
  // per row so both the GP column and GP-sort use the windowed value.
  const effectiveStats = useCallback((p: PlayerRow): SeasonStats | null => {
    if (pointsTimeFilter === 'season' || !p.stats) return p.stats;
    return { ...p.stats, games_played: gpMap.get(p.id) ?? 0 };
  }, [pointsTimeFilter, gpMap]);

  // Sort
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find(c => c.key === sortKey);
    if (!col) {
      // Sort by sabotage pts
      if (sortKey === 'SPTS') {
        return [...filtered].sort((a, b) => {
          let av = getTP(a);
          let bv = getTP(b);
          if (statView === 'pergame') {
            const gpA = gpMap.get(a.id) || a.stats?.games_played || 0;
            const gpB = gpMap.get(b.id) || b.stats?.games_played || 0;
            av = gpA > 0 ? av / gpA : 0;
            bv = gpB > 0 ? bv / gpB : 0;
          }
          return sortDir === 'desc' ? bv - av : av - bv;
        });
      }
      return filtered;
    }
    return [...filtered].sort((a, b) => {
      let av = col.getValue(effectiveStats(a));
      let bv = col.getValue(effectiveStats(b));
      if (typeof av === 'string') av = 0;
      if (typeof bv === 'string') bv = 0;
      if (statView === 'pergame' && !col.isRate) {
        const gpA = a.stats?.games_played || 1;
        const gpB = b.stats?.games_played || 1;
        av = (av as number) / gpA;
        bv = (bv as number) / gpB;
      }
      return sortDir === 'desc' ? (bv as number) - (av as number) : (av as number) - (bv as number);
    });
  }, [filtered, sortKey, sortDir, columns, statView, tpMap, gpMap, pointsTimeFilter, effectiveStats]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Rank', 'Player', 'Position', 'Team', ...columns.map(c => c.label), 'SABOTAGE PTS'];
    const rows = sorted.map((p, i) => [
      i + 1, `"${p.full_name}"`, p.position_abbreviation, p.mlb_team_abbreviation || 'FA',
      ...columns.map(c => {
        const v = c.getValue(p.stats);
        return typeof v === 'number' ? v.toFixed(1) : v;
      }),
      getTP(p).toFixed(1),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '2026-projections.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getRankBadge = (rank: number) => {
    // MLB-066 — dropped the crown for rank #1; consistent text rank with tiered colors. Crown was small/easy-to-miss and inconsistent with the rest of the rank column being text.
    if (rank <= 3) return <span className="text-gold font-black">#{rank}</span>;
    if (rank <= 10) return <span className="text-primary font-bold">#{rank}</span>;
    if (rank <= 50) return <span className="text-muted-foreground font-semibold">#{rank}</span>;
    return <span className="text-muted-foreground/60">#{rank}</span>;
  };

  const handleAddClick = (player: { id: string; full_name: string; position_abbreviation: string; position_eligibility?: string[] | null; mlb_team_abbreviation: string | null; headshot_url: string | null; player_status?: string }) => {
    setAddTarget(player);
    setDropTarget(null);
    setAddDropOpen(true);
  };

  const handleDropClick = (playerId: string) => {
    const rp = addDrop.getMyRosterPlayer(playerId);
    if (!rp) return;
    setDropTarget(rp);
    setAddTarget(null);
    setAddDropOpen(true);
  };

  // Countdown helper for waiver wire badges
  const getWaiverCountdown = (playerId: string) => {
    const expiresAt = addDrop.getWaiverExpiry(playerId);
    if (!expiresAt) return null;
    const diffMs = new Date(expiresAt).getTime() - Date.now();
    if (diffMs <= 0) return null;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Ownership badge
  const OwnershipBadge = ({ playerId }: { playerId: string }) => {
    if (addDrop.isOnMyTeam(playerId)) {
      return <Badge variant="secondary" className="text-[9px] px-1.5 h-4 bg-primary/15 text-primary border-primary/20">My Team</Badge>;
    }
    const owner = addDrop.getOwnerTeamName(playerId);
    if (owner) {
      return <Badge variant="outline" className="text-[9px] px-1.5 h-4 text-muted-foreground">{owner}</Badge>;
    }
    if (addDrop.isOnWaiverWire(playerId)) {
      const countdown = getWaiverCountdown(playerId);
      return (
        <Badge variant="outline" className="text-[9px] px-1.5 h-4 text-amber-400 border-amber-400/30">
          WW{countdown ? ` ${countdown}` : ''}
        </Badge>
      );
    }
    return <Badge variant="outline" className="text-[9px] px-1.5 h-4 text-success border-success/30">FA</Badge>;
  };

  // Action button
  const PlayerAction = ({ player }: { player: { id: string; full_name: string; position_abbreviation: string; position_eligibility?: string[] | null; mlb_team_abbreviation: string | null; headshot_url: string | null; status?: string } }) => {
    if (addDrop.rosterLocked) return <Lock className="w-3.5 h-3.5 text-muted-foreground" />;
    if (!addDrop.myTeam) return null;
    if (addDrop.isAcquisitionLimitReached && addDrop.isFreeAgent(player.id)) {
      return <Lock className="w-3.5 h-3.5 text-muted-foreground" />;
    }
    if (addDrop.isFreeAgent(player.id)) {
      // Only show "Claim" if player is on active waiver wire in a FAAB league
      const isClaim = addDrop.isOnWaiverWire(player.id) && (waiverMode === 'faab_24' || waiverMode === 'faab_48');
      return (
        <Button
          size="sm"
          variant="ghost"
          className="h-9 sm:h-7 px-2 gap-1 text-xs text-primary hover:text-primary"
          aria-label={isClaim ? 'Claim player' : 'Add player'}
          title={isClaim ? 'Claim' : 'Add'}
          onClick={(e) => { e.stopPropagation(); handleAddClick({ ...player, player_status: player.status }); }}
        >
          <UserPlus className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
          <span className="hidden sm:inline">{isClaim ? 'Claim' : 'Add'}</span>
        </Button>
      );
    }
    if (addDrop.isOnMyTeam(player.id)) {
      return (
        <Button
          size="sm"
          variant="ghost"
          className="h-9 sm:h-7 px-2 gap-1 text-xs text-destructive hover:text-destructive"
          aria-label="Drop player"
          title="Drop"
          onClick={(e) => { e.stopPropagation(); handleDropClick(player.id); }}
        >
          <UserMinus className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
          <span className="hidden sm:inline">Drop</span>
        </Button>
      );
    }
    const ownerName = addDrop.getOwnerTeamName(player.id);
    return (
      <Button
        size="sm"
        variant="ghost"
        className="h-9 sm:h-7 px-2 gap-1 text-xs text-primary hover:text-primary"
        aria-label={`Trade with ${ownerName}`}
        title={`Trade with ${ownerName}`}
        onClick={(e) => { e.stopPropagation(); onNavigateToTrades?.(); }}
      >
        <ArrowLeftRight className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        <span className="hidden sm:inline">Trade</span>
      </Button>
    );
  };

  // Build StatsRows for StatsColumns
  const statsRows: StatsRow[] = sorted.map(p => ({
    id: p.id,
    stats: effectiveStats(p),
    sabPts: getTP(p),
    filteredGamesPlayed: pointsTimeFilter !== 'season' ? gpMap.get(p.id) : undefined,
  }));

  // Row virtualization — only render rows in the viewport. Unblocks slower
  // Android devices (Pixel 8 etc.) from rendering ~1,300 rows at once.
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const ROW_HEIGHT = 44; // matches min-h-11 / h-11
  const virtualizer = useWindowVirtualizer({
    count: sorted.length,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
    scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0
    ? virtualItems[0].start - (tableContainerRef.current?.offsetTop ?? 0)
    : 0;
  const paddingBottom = virtualItems.length > 0
    ? totalSize - (virtualItems[virtualItems.length - 1].end - (tableContainerRef.current?.offsetTop ?? 0))
    : 0;
  const visibleStatsRows = virtualItems.map(vi => statsRows[vi.index]).filter(Boolean);
  const visibleStartIndex = virtualItems[0]?.index ?? 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold font-heading text-foreground">Players</h1>
        <div className="flex items-center gap-2">
          {addDrop.myTeam && addDrop.maxAcquisitions > 0 && (
            <Badge variant={addDrop.isAcquisitionLimitReached ? "destructive" : "secondary"} className="text-xs">
              {addDrop.acquisitionsUsed}/{addDrop.maxAcquisitions} moves
            </Badge>
          )}
          {addDrop.myTeam && waiverMode !== 'fcfs' && (
            <Badge variant="secondary" className="text-xs">
              ${addDrop.faabRemaining} FAAB
            </Badge>
          )}
          {/* Compare button: gates checkbox-column visibility. Default state shows
              "Compare" — clicking enters compare mode and reveals the checkbox column
              on every row. Once 2+ players are selected, the button becomes the
              dialog opener. With <2 selected, the button reverts to "Cancel" to exit
              compare mode + clear the selection. */}
          {(() => {
            const count = selectedPlayerIds.size;
            if (!compareMode) {
              return (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 text-xs"
                  onClick={() => setCompareMode(true)}
                  title="Enable compare mode to select 2 or more players"
                >
                  <GitCompareArrows className="w-3.5 h-3.5" />
                  Compare
                </Button>
              );
            }
            if (count >= 2) {
              return (
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 gap-1.5 text-xs"
                  onClick={() => setCompareOpen(true)}
                  title={`Compare ${count} selected players`}
                >
                  <GitCompareArrows className="w-3.5 h-3.5" />
                  Compare {count} selected →
                </Button>
              );
            }
            return (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1.5 text-xs"
                onClick={() => { setCompareMode(false); setSelectedPlayerIds(new Set()); }}
                title="Cancel compare mode"
              >
                {count === 1 ? 'Cancel (select 1 more)' : 'Cancel'}
              </Button>
            );
          })()}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Input
          placeholder="Search players or teams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 bg-card border-border"
        />
        {/* Mobile-only Export — Compact Grid spec moves Export off the filter rows entirely.
            Desktop Export still lives in the wrapping toolbar below. */}
        <Button
          variant="outline"
          size="icon"
          onClick={handleExportCSV}
          className="sm:hidden h-10 w-10 shrink-0"
          aria-label="Export CSV"
          title="Export CSV"
        >
          <Download className="w-4 h-4" />
        </Button>
      </div>

      {/* MOBILE filter grid — Compact Grid (Variation C). Two rows of full-width segmented
          controls; ghost-outline selected style; no inline labels. Hidden at sm+ in favor
          of the existing wrapping toolbar below. */}
      <div className="sm:hidden space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Seg
            ariaLabel="Position filter"
            value={({ all: 'All', batters: 'Batters', pitchers: 'Pitchers' } as const)[playerType]}
            options={['All', 'Batters', 'Pitchers']}
            onChange={(v) => {
              const map = { All: 'all', Batters: 'batters', Pitchers: 'pitchers' } as const;
              setPlayerType(map[v as keyof typeof map]);
              setSortKey('SPTS');
              setSortDir('desc');
            }}
          />
          <Seg
            ariaLabel="Ownership filter"
            value={({ all: 'All', available: 'Available', taken: 'Taken' } as const)[ownershipFilter]}
            options={['All', 'Available', 'Taken']}
            onChange={(v) => {
              const map = { All: 'all', Available: 'available', Taken: 'taken' } as const;
              setOwnershipFilter(map[v as keyof typeof map]);
            }}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Seg
            ariaLabel="Range filter"
            value={({ '7d': '7D', '14d': '14D', '30d': '30D', season: 'Season' } as const)[pointsTimeFilter]}
            options={['7D', '14D', '30D', 'Season']}
            onChange={(v) => {
              const map = { '7D': '7d', '14D': '14d', '30D': '30d', Season: 'season' } as const;
              setPointsTimeFilter(map[v as keyof typeof map] as PointsTimeFilter);
              setSortKey('SPTS');
              setSortDir('desc');
            }}
          />
          <Seg
            ariaLabel="View filter"
            value={statView === 'pergame' ? 'Per Game' : 'Totals'}
            options={['Totals', 'Per Game']}
            onChange={(v) => setStatView(v === 'Per Game' ? 'pergame' : 'season')}
          />
        </div>
      </div>

      {/* Granular position — single Select instance, visible at all sizes.
          One source of truth for `posFilter` so trigger duplication can't drift.
          Trigger is full-width on mobile (search input owns its row above) and
          inline-sized on desktop (slots above the wrapping toolbar). */}
      <Select value={posFilter} onValueChange={setPosFilter}>
        <SelectTrigger className="w-full sm:w-32 bg-card border-border h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {POSITION_GROUPS.map(p => (
            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <p className="sm:hidden text-xs text-muted-foreground">
        {filtered.length.toLocaleString()} players · sorted by {sortKey === 'SPTS' || !sortKey ? 'SAB' : sortKey}
        {rankingsLoading && pointsTimeFilter !== 'season' && ' · loading…'}
      </p>

      {/* DESKTOP toolbar — wrapping pills + Export. Hidden on mobile (replaced by Seg grid above). */}
      <div className="hidden sm:flex sm:items-center sm:gap-3 sm:flex-wrap">
        <Tabs className="-mx-4 sm:mx-0" value={playerType} onValueChange={(v) => { setPlayerType(v as 'all' | 'batters' | 'pitchers'); setSortKey('SPTS'); setSortDir('desc'); }}>
          <TabsList className="flex w-full p-0 rounded-none [&>button]:flex-1 [&>button:first-child]:rounded-l-none [&>button:last-child]:rounded-r-none sm:inline-flex sm:w-auto sm:p-1 sm:rounded-md sm:[&>button:first-child]:rounded-l-sm sm:[&>button:last-child]:rounded-r-sm sm:[&>button]:flex-initial h-8">
            <TabsTrigger value="all" className="text-xs px-2 sm:px-3 h-7">All</TabsTrigger>
            <TabsTrigger value="batters" className="text-xs px-2 sm:px-3 h-7">Batters</TabsTrigger>
            <TabsTrigger value="pitchers" className="text-xs px-2 sm:px-3 h-7">Pitchers</TabsTrigger>
          </TabsList>
        </Tabs>
        {/* Collapsed back to 3-state per Adam's mobile UX feedback — "On my team" /
            "On other teams" rarely needed and the 4 chips cramped the mobile filter
            row. "Taken" covers both. */}
        <Tabs className="-mx-4 sm:mx-0" value={ownershipFilter} onValueChange={(v) => setOwnershipFilter(v as 'all' | 'available' | 'taken')}>
          <TabsList className="flex w-full p-0 rounded-none [&>button]:flex-1 [&>button:first-child]:rounded-l-none [&>button:last-child]:rounded-r-none sm:inline-flex sm:w-auto sm:p-1 sm:rounded-md sm:[&>button:first-child]:rounded-l-sm sm:[&>button:last-child]:rounded-r-sm sm:[&>button]:flex-initial h-8">
            <TabsTrigger value="all" className="text-xs px-2 sm:px-3 h-7">All</TabsTrigger>
            <TabsTrigger value="available" className="text-xs px-2 sm:px-3 h-7">Available</TabsTrigger>
            <TabsTrigger value="taken" className="text-xs px-2 sm:px-3 h-7">Taken</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs className="-mx-4 sm:mx-0" value={statView} onValueChange={(v) => setStatView(v as 'season' | 'pergame')}>
          <TabsList className="flex w-full p-0 rounded-none [&>button]:flex-1 [&>button:first-child]:rounded-l-none [&>button:last-child]:rounded-r-none sm:inline-flex sm:w-auto sm:p-1 sm:rounded-md sm:[&>button:first-child]:rounded-l-sm sm:[&>button:last-child]:rounded-r-sm sm:[&>button]:flex-initial h-8">
            <TabsTrigger value="season" className="text-xs px-2 sm:px-3 h-7">Totals</TabsTrigger>
            <TabsTrigger value="pergame" className="text-xs px-2 sm:px-3 h-7">Per Game</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs className="-mx-4 sm:mx-0" value={pointsTimeFilter} onValueChange={(v) => { setPointsTimeFilter(v as PointsTimeFilter); setSortKey('SPTS'); setSortDir('desc'); }}>
          <TabsList className="flex w-full p-0 rounded-none [&>button]:flex-1 [&>button:first-child]:rounded-l-none [&>button:last-child]:rounded-r-none sm:inline-flex sm:w-auto sm:p-1 sm:rounded-md sm:[&>button:first-child]:rounded-l-sm sm:[&>button:last-child]:rounded-r-sm sm:[&>button]:flex-initial h-8">
            {TIME_FILTER_OPTIONS.map(opt => (
              <TabsTrigger key={opt.value} value={opt.value} className="text-xs px-2 sm:px-3 h-7">{opt.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {rankingsLoading && pointsTimeFilter !== 'season' && (
          <span className="text-xs text-muted-foreground animate-pulse">Loading...</span>
        )}
        {/* Compact Grid spec drops the standalone "Last N Days ✕" chip — redundant with the
            Range control. Result-count line on mobile + active Range tab on desktop are the
            sources of truth for "I've narrowed this." */}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleExportCSV} className="h-7 text-xs gap-1">
            <Download className="w-3 h-3" />
            Export
          </Button>
        </div>
      </div>

      {/* MLB-062 — search has matches in the sibling tab; offer to widen */}
      {crossTabMatch && (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 flex items-center justify-between gap-2 text-xs">
          <span className="text-foreground">
            <span className="font-semibold">{crossTabMatch.count}</span> more {crossTabMatch.other} {crossTabMatch.count === 1 ? 'matches' : 'match'} <span className="font-mono text-primary">"{search}"</span>.
          </span>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-primary"
            onClick={() => setPlayerType('all')}>
            Switch to All →
          </Button>
        </div>
      )}

      {/* ESPN-style table */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center space-y-3">
          <p className="text-sm font-semibold text-destructive">Failed to load players.</p>
          <p className="text-xs text-muted-foreground">
            {error.message || 'A network or server error occurred.'}
          </p>
          <Button size="sm" onClick={() => setLoadAttempt(n => n + 1)}>
            Tap to retry
          </Button>
        </div>
      ) : (
        <div ref={tableContainerRef} className="rounded-lg border border-border overflow-x-auto">
          <div className="flex">
            {/* Sticky left: player info */}
            <div className="sticky left-0 flex-shrink-0 z-10 bg-card border-r border-border">
              {/* Header */}
              <div className="flex items-center bg-muted/50 text-muted-foreground text-[10px] font-semibold uppercase tracking-wider border-b border-border h-9">
                {/* MLB-068 — checkbox column header; only rendered when compare mode is engaged */}
                {compareMode && (
                  <div className="w-7 px-1 flex items-center justify-center"></div>
                )}
                <div className="w-9 text-center px-1">#</div>
                <div className="min-w-[180px] sm:min-w-[240px] flex-1 px-2">Player</div>
                <div className="w-16 text-center px-1 hidden sm:block">Owner</div>
                {/* MLB-061 — mobile-only inline SAB PTS column so users see scoring without horizontal scroll */}
                <div className="w-14 text-right px-1 sm:hidden tabular-nums">SAB</div>
                <div className="w-12 text-center px-1"></div>
              </div>
              {paddingTop > 0 && <div style={{ height: paddingTop }} />}
              {/* Rows — virtualized */}
              {virtualItems.map(vi => {
                const player = sorted[vi.index];
                if (!player) return null;
                const idx = vi.index;
                return (
                <div
                  key={player.id}
                  onClick={() => onNavigateToPlayer?.(player.id)}
                  className={cn(
                    "flex items-center border-b border-border/40 cursor-pointer hover:bg-muted/30 transition-colors min-h-11",
                    idx % 2 === 1 && "bg-muted/10",
                    compareMode && selectedPlayerIds.has(player.id) && "bg-primary/10 hover:bg-primary/15"
                  )}
                >
                  {/* MLB-068 — selection checkbox; stopPropagation so toggling doesn't navigate to the player page; only rendered when compare mode is engaged */}
                  {compareMode && (
                    <div className="w-7 px-1 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedPlayerIds.has(player.id)}
                        onCheckedChange={() => togglePlayerSelection(player.id)}
                        aria-label={`Select ${player.full_name} for compare`}
                      />
                    </div>
                  )}
                  <div className="w-9 text-center px-1 text-xs">{getRankBadge(idx + 1)}</div>
                  <div className="min-w-[180px] sm:min-w-[240px] flex-1 px-2 flex items-center gap-2">
                    <MLBPlayerAvatar
                      name={player.full_name}
                      headshotUrl={player.headshot_url}
                      position={player.position_abbreviation}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-medium text-foreground block truncate">{player.full_name}</span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {/* MLB-067 — three distinct typographic treatments: role in colored chip, team+matchup as plain small text ("vs LAA"), LIVE as brand pulse-pill (matches the dashboard live matchup card energy). Don't pillify everything. */}
                        <Badge variant="outline" className={cn("text-[8px] px-1 py-0 h-3.5", positionColors[player.position_abbreviation])}>
                          {formatPositionDisplay(player.position_abbreviation, player.position_eligibility)}
                        </Badge>
                        <span className="text-[9px] text-muted-foreground">{player.mlb_team_abbreviation || 'FA'}</span>
                        {(() => {
                          const gi = player.mlb_team_abbreviation ? gameInfoMap.get(player.mlb_team_abbreviation) : undefined;
                          if (!gi) return null;
                          if (gi.status === 'live' || gi.status === 'In Progress') {
                            return (
                              <Badge variant="outline" className="text-[8px] px-1.5 py-0 h-3.5 bg-primary text-primary-foreground border-primary animate-pulse font-semibold shrink-0">
                                ● LIVE
                              </Badge>
                            );
                          }
                          return (
                            <span className={cn("text-[9px]",
                              gi.status === 'Final' || gi.status === 'Game Over' ? "text-muted-foreground/60" : "text-muted-foreground/70"
                            )}>
                              {gi.status === 'Final' || gi.status === 'Game Over' ? 'Final' : `${gi.isHome ? 'vs' : '@'} ${gi.opponent}`}
                            </span>
                          );
                        })()}
                        <ProbablePitcherBadge startInfo={byPlayerId.get(player.id) || null} compact />
                        {player.mlb_id && probablePitcherMlbIds.has(player.mlb_id) && (
                          <Badge variant="outline" className="text-[8px] px-1 py-0 h-3.5 border-orange-400/60 text-orange-500 bg-orange-500/10 font-semibold shrink-0">PP</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-16 text-center px-1 hidden sm:flex justify-center">
                    <OwnershipBadge playerId={player.id} />
                  </div>
                  <div className="w-14 text-right px-1 sm:hidden tabular-nums font-bold text-primary text-sm">
                    {(() => {
                      const tp = getTP(player);
                      const gp = gpMap.get(player.id) || player.stats?.games_played || 0;
                      const v = statView === 'pergame' && gp > 0 ? tp / gp : tp;
                      return v.toFixed(1);
                    })()}
                  </div>
                  <div className="w-12 text-center px-1 flex justify-center">
                    <PlayerAction player={{
                      id: player.id,
                      full_name: player.full_name,
                      position_abbreviation: player.position_abbreviation,
                      // SAB-279 — propagate full eligibility so add/drop confirm respects multi-position players
                      position_eligibility: player.position_eligibility,
                      mlb_team_abbreviation: player.mlb_team_abbreviation,
                      headshot_url: player.headshot_url,
                      status: player.status,
                    }} />
                  </div>
                </div>
                );
              })}
              {paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
              {sorted.length === 0 && (
                <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">
                  {posFilter === 'TwoStart'
                    ? 'No two-start pitchers confirmed yet. MLB announces probable pitchers ~3 days ahead'
                    : 'No players found'}
                </div>
              )}
            </div>

            <StatsColumns
              rows={visibleStatsRows}
              columns={columns}
              statView={statView}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
              startIndex={visibleStartIndex}
              paddingTop={paddingTop}
              paddingBottom={paddingBottom}
            />
          </div>
        </div>
      )}

      <AddDropDialog
        open={addDropOpen}
        onOpenChange={setAddDropOpen}
        addPlayer={addTarget}
        dropPlayer={dropTarget}
        roster={addDrop.roster}
        faabRemaining={addDrop.faabRemaining}
        processing={addDrop.processing}
        onConfirm={addDrop.executeAddDrop}
        onAdd={addDrop.executeAdd}
        onAddToIL={addDrop.executeAddToIL}
        onSubmitClaim={(playerId, faabBid, dropPlayerId) => waiverClaims.submitClaim(playerId, faabBid, dropPlayerId, undefined, addTarget ? addDrop.getWaiverWireId(addTarget.id) || undefined : undefined)}
        waiverMode={waiverMode}
        isOnWaiverWire={addTarget ? addDrop.isOnWaiverWire(addTarget.id) : false}
        waiverExpiresAt={addTarget ? addDrop.getWaiverExpiry(addTarget.id) : null}
        checkPositionLimit={addDrop.checkPositionLimit}
        checkSlotEligibility={addDrop.checkSlotEligibility}
        isAddPlayerILEligible={addTarget ? addDrop.isFreeAgentILEligible(addTarget) : false}
        availableILSlots={addDrop.availableILSlots}
        openActiveSlots={addDrop.openActiveSlots}
        totalRosterSize={addDrop.totalRosterSize}
      />
      <PlayerCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        initialPlayerIdA={Array.from(selectedPlayerIds)[0] || null}
        initialPlayerIdB={Array.from(selectedPlayerIds)[1] || null}
      />
    </div>
  );
};

export default MLBBetaPlayers;
